import { Box, Stack, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import type { LLMClassifierApprovalReviewer } from "models/LLMs/LLMClassifierApproval";
import { ApprovalChangeSummary } from "./ApprovalChangeSummary";

export function RequestApprovalDialog(props: {
    isOpen: boolean;
    onClose: () => void;
    availableReviewers: LLMClassifierApprovalReviewer[];
    selectedReviewers: LLMClassifierApprovalReviewer[];
    onReviewersChange: (reviewers: LLMClassifierApprovalReviewer[]) => void;
    note: string;
    onNoteChange: (note: string) => void;
    before?: LLMClassifierImplementation;
    after: LLMClassifierImplementation;
    isLoadingReviewers: boolean;
    isSaving: boolean;
    onSubmit: () => void;
}) {
    const reviewerOptions = props.availableReviewers.map((reviewer) => {
        const identifier = reviewerIdentifier(reviewer);
        return {
            ...reviewer,
            optionLabel: identifier
                ? `${reviewer.displayName} (${identifier})`
                : reviewer.displayName,
        };
    });

    return (
        <AcxDialog
            isOpen={props.isOpen}
            onClose={props.onClose}
            title="Request approval"
            hideCancelButton
            buttonsDisabled={props.isSaving}
            dialogContentSx={{ maxHeight: "70vh" }}
            dialogContentChildren={
                <Stack gap={3}>
                    <Box>
                        <AcxSelectMulti
                            id="approval-reviewers"
                            options={reviewerOptions}
                            valueField="userId"
                            labelField="optionLabel"
                            inputLabel="Approvers (optional)"
                            placeholder={
                                props.isLoadingReviewers
                                    ? "Loading reviewers..."
                                    : "Select one or more approvers"
                            }
                            defaultValue={props.selectedReviewers}
                            onChange={(reviewers) =>
                                props.onReviewersChange(reviewers ?? [])
                            }
                            isClearable
                            isLoading={props.isLoadingReviewers}
                            isDisabled={props.isLoadingReviewers}
                            containerHeight="auto"
                            maxContainerHeight="96px"
                            alignControls="flex-start"
                            fullWidth
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            mt={0.5}
                            display="block"
                        >
                            Only users with Approver permission appear here.
                            Leave this blank to request approval from any
                            approver. You're excluded. Self-approval isn't
                            allowed.
                        </Typography>
                    </Box>
                    <AcxMainTextField
                        id="approval-request-note"
                        labelText="Note for the approvers (optional)"
                        value={props.note}
                        onChange={(event) =>
                            props.onNoteChange(event.target.value)
                        }
                        multiline
                        rows={3}
                        maxLength={2000}
                        fullWidth
                    />
                    <Box>
                        <Typography fontWeight={600} variant="body2" mb={1}>
                            Approvers will see
                        </Typography>
                        <ApprovalChangeSummary
                            before={props.before}
                            after={props.after}
                        />
                    </Box>
                </Stack>
            }
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <Typography variant="body2" color="gray.600">
                    Logged to the change record
                </Typography>
                <Stack direction="row" gap={1}>
                    <AcxButton color="white" onClick={props.onClose}>
                        Cancel
                    </AcxButton>
                    <AcxButton
                        loading={props.isSaving}
                        disabled={props.isLoadingReviewers}
                        onClick={props.onSubmit}
                    >
                        Send request
                    </AcxButton>
                </Stack>
            </Stack>
        </AcxDialog>
    );
}

function reviewerIdentifier(reviewer: LLMClassifierApprovalReviewer) {
    const identifier = reviewer.email ?? reviewer.userName;
    return identifier?.localeCompare(reviewer.displayName, undefined, {
        sensitivity: "accent",
    }) === 0
        ? undefined
        : identifier;
}
